<template>
  <v-row>
    <v-col cols="auto" class="pa-0">
      <v-btn text @click="goBack" color="secondary" class="font-weight-bold">
        <v-icon class="mr-2">
          keyboard_arrow_left
        </v-icon>
        Go Back
      </v-btn>
    </v-col>
    <v-col cols="12">
      <survey-card :survey="survey" disable-expansion></survey-card>
    </v-col>
    <template v-if="this.survey.sentInstancesCount == 0">
      <v-col cols="12">
        <v-card>
          <v-row>
            <v-col cols="auto">
              <v-img :src="$images.WAITING" width="150" class="mx-6"></v-img>
            </v-col>
            <v-col cols="9">
              <v-card-title>
                We're waiting on your survey results!
              </v-card-title>
              <v-card-subtitle>
                This survey hasn't collected any answers at this time. Come back
                later!
              </v-card-subtitle>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </template>
    <template v-else>
      <v-col cols="12" v-if="survey.isAnonymous">
        <v-container class="py-0">
          <v-row class="white border--primary rounded primary--text ">
            <v-col cols="auto">
              <v-img
                :src="$images.COLLECTION_METHOD_ANONYMOUS"
                width="30"
              ></v-img>
            </v-col>
            <v-col class="my-auto">
              This is an anonymous survey and no individuals will be identified
              in the results
            </v-col>
          </v-row>
        </v-container>
      </v-col>
      <v-col cols="12" sm="6" md="4" class="px-0 pt-0">
        <v-col cols="12">
          <reporting-filter
            v-model="filter"
            v-on:change="loadData"
            v-if="survey.id"
            :isAnonymous="survey.isAnonymous"
          ></reporting-filter>
        </v-col>
      </v-col>
      <v-col cols="12" sm="6" md="8" class="px-0 pt-0">
        <v-col cols="12">
          <engagement-card
            :loading="loading"
            data="reporting/overview"
            horizontal
            v-on:click="selectUser"
          ></engagement-card>
        </v-col>
        <template v-if="loading">
          <v-col cols="12" v-for="i in 2" :key="i">
            <v-skeleton-loader
              type="list-item-avatar,divider,image"
            ></v-skeleton-loader>
          </v-col>
        </template>
        <template
          v-else-if="
            overview.overallUserRecords.finished <= 3 && survey.isAnonymous
          "
        >
          <v-col cols="12">
            <v-card>
              <v-card-text class="py-0">
                <v-row>
                  <v-col cols="auto" class="my-auto">
                    <v-img
                      :src="$images.COLLECTION_METHOD_ANONYMOUS"
                      width="60"
                    ></v-img>
                  </v-col>
                  <v-col class="my-auto">
                    <v-card-title>
                      Blocked by the anonymous squirrel
                    </v-card-title>
                    <v-card-text>
                      We require a minimum of 3 respondents to protect a members
                      identity. Adjust your filters or wait for more data to
                      come in
                    </v-card-text>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </template>
        <template v-else-if="overview.overallUserRecords.finished > 0">
          <v-col
            cols="12"
            v-for="(question, i) in questions"
            :key="question.id"
          >
            <v-skeleton-loader v-if="loading" type="image" class="full-height">
            </v-skeleton-loader>
            <v-card v-else>
              <v-card-title>
                {{ i + 1 }}. {{ question.question }}
                <v-spacer></v-spacer>
                <v-icon
                  class="mr-2"
                  :color="getQuestionTypeColor(question.questionTypeId)"
                >
                  {{ getQuestionTypeIcon(question.questionTypeId) }}
                </v-icon>
                <span
                  v-bind:style="{
                    color: getQuestionTypeColor(question.questionTypeId)
                  }"
                  >{{ question.questionTypeName }}</span
                >
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text>
                <template
                  v-if="
                    question.questionTypeId ==
                      $enums.questionTypes.MULTIPLE_CHOICE
                  "
                >
                  <survey-multiple-choice-results
                    :answers="question.multipleChoiceAnswers"
                    v-on:answer="showAnswerUsers(question, $event)"
                    :isAnonymous="survey.isAnonymous"
                  ></survey-multiple-choice-results>
                </template>
                <survey-opinion-scale-results
                  v-if="
                    question.questionTypeId ==
                      $enums.questionTypes.OPINION_SCALE
                  "
                  :answers="question.opinionScaleAnswers"
                  v-on:answer="showAnswerUsers(question, $event)"
                  :scaleRightLabel="question.scaleRightLabel"
                  :scaleLeftLabel="question.scaleLeftLabel"
                ></survey-opinion-scale-results>
                <survey-text-results
                  v-if="
                    question.questionTypeId ==
                      $enums.questionTypes.TEXT_RESPONSE
                  "
                  :questionId="question.id"
                  :filter="filter"
                  :survey="survey"
                ></survey-text-results>
              </v-card-text>
            </v-card>
          </v-col>
        </template>
      </v-col>
    </template>
    <survey-answer-users-dialog
      ref="answerUsers"
      :surveyId="survey.id"
    ></survey-answer-users-dialog>
  </v-row>
</template>

<script>
import { mapGetters } from "vuex";
import questionTypes from "@/helpers/mixins/questionTypes";
import ReportingFilter from "@/components/reporting/ReportingFilter";
import EngagementCard from "@/components/reporting/EngagementCard";
import SurveyMultipleChoiceResults from "@/components/reporting/SurveyMultipleChoiceResults";
import SurveyOpinionScaleResults from "@/components/reporting/SurveyOpinionScaleResults";
import SurveyTextResults from "@/components/reporting/SurveyTextResults";
import SurveyAnswerUsersDialog from "@/components/reporting/SurveyAnswerUsersDialog";
import SurveyCard from "@/components/surveys/SurveyCard";
export default {
  name: "SurveyReport",
  components: {
    "reporting-filter": ReportingFilter,
    EngagementCard,
    "survey-multiple-choice-results": SurveyMultipleChoiceResults,
    "survey-opinion-scale-results": SurveyOpinionScaleResults,
    "survey-text-results": SurveyTextResults,
    SurveyAnswerUsersDialog,
    SurveyCard
  },
  mixins: [questionTypes],
  props: {
    filter: { type: Object },
    surveyId: { type: String }
  },
  data: () => ({
    loading: false
  }),
  computed: {
    ...mapGetters({
      questions: "reporting/survey_questions",
      survey: "reporting/survey",
      overview: "reporting/overview"
    })
  },
  created() {
    this.loadData();
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    loadData() {
      this.loading = true;
      Promise.all([
        this.$store.dispatch("reporting/getSurveyReport", {
          surveyId: this.surveyId,
          filter: this.filter
        })
      ]).finally(() => {
        this.loading = false;
      });
    },
    showAnswerUsers(question, answer) {
      if (this.survey.isAnonymous) {
        return;
      }
      this.$refs.answerUsers.showDialog(question, answer, this.filter);
    },
    selectUser(userId) {
      this.filter.userId = userId;
      this.loadData();
    }
  }
};
</script>
